import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { dealerAuth, setDealerToken } from '../../services/dealerApi';
import { toast } from '../../hooks/use-toast';

const DealerLogin = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ name: '', email: '', mobile: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = mode === 'login'
        ? await dealerAuth.login({ email: form.email, password: form.password })
        : await dealerAuth.register({ name: form.name, email: form.email, password: form.password, mobile: form.mobile });
      setDealerToken(res.token);
      toast({ title: mode === 'login' ? 'Welcome back!' : 'Account created' });
      navigate(res.dealer.profile_completed ? '/dealers/dashboard' : '/dealers/profile');
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Please try again', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'bg-slate-950/60 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 focus-visible:ring-0';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center px-4">
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(6,182,212,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(59,130,246,0.1),transparent_50%)]" />
      </div>
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center gap-3 mb-2">
            <img
              src="/brand/propitstop-drive.png"
              alt="Propitstop Drive"
              data-testid="propitstop-drive-logo"
              className="w-40 h-40 sm:w-48 sm:h-48 object-contain drop-shadow-[0_0_30px_rgba(6,182,212,0.25)]"
            />
          </Link>
          <p className="mt-1 text-sm text-slate-400">{mode === 'login' ? 'Sign in to your dealer dashboard' : 'Create your dealer account'}</p>
        </div>

        <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1">Dealer Name *</label>
                  <Input data-testid="dealer-name-input" required placeholder="ABC Motors" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1">Mobile Number *</label>
                  <Input data-testid="dealer-mobile-input" required maxLength={10} placeholder="9999999999" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className={inputCls} />
                </div>
              </>
            )}
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1">Email *</label>
              <Input data-testid="dealer-email-input" type="email" required placeholder="dealer@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1">Password *</label>
              <Input data-testid="dealer-password-input" type="password" required minLength={6} placeholder="Min 6 characters" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
            </div>
            <Button type="submit" disabled={loading} data-testid="dealer-submit-btn"
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-slate-950 font-bold py-5 rounded-xl shadow-[0_0_20px_rgba(6,182,212,0.25)] hover:shadow-[0_0_30px_rgba(6,182,212,0.45)] transition-[box-shadow,background-color] duration-300">
              {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>

          <div className="mt-5 pt-5 border-t border-slate-800 text-center">
            <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')} className="text-sm text-slate-400 hover:text-cyan-300 transition-colors" data-testid="dealer-mode-toggle">
              {mode === 'login' ? "Don't have an account? Register" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealerLogin;
